import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import i18n, { SUPPORTED_LANGUAGES } from "@/i18n";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Languages } from "lucide-react";
import { MARKETING_LANGS, MarketingLang, splitLangPath, withLang } from "@/lib/langRoutes";

interface LanguageSwitcherProps {
  /**
   * On the public marketing site the switcher must be crawlable: it renders
   * real <a href="/en"> / <a href="/fr"> / <a href="/"> anchors instead of a
   * JavaScript-only state change.
   */
  asLinks?: boolean;
}

export const LanguageSwitcher = ({ asLinks = false }: LanguageSwitcherProps) => {
  const { i18n: i } = useTranslation();
  const { pathname } = useLocation();
  const { basePath } = splitLangPath(pathname);

  const isRouted = (code: string): code is MarketingLang =>
    (MARKETING_LANGS as readonly string[]).includes(code);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 px-2 sm:px-3">
          <Languages className="h-4 w-4" />
          <span className="hidden sm:inline">
            {SUPPORTED_LANGUAGES.find((l) => l.code === i.language)?.label || i.language}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SUPPORTED_LANGUAGES.map((lang) =>
          asLinks && isRouted(lang.code) ? (
            <DropdownMenuItem key={lang.code} asChild>
              <a href={withLang(basePath, lang.code) || "/"} hrefLang={lang.code}>
                {lang.label}
              </a>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem key={lang.code} onClick={() => i18n.changeLanguage(lang.code)}>
              {lang.label}
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
